import React from 'react';
import { Plus, Award, Globe, Briefcase, Loader2, X, Upload, Image } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const CoachForm = ({
  formData,
  activeTab,
  setActiveTab,
  imagePreview,
  isSubmitting,
  fileInputRef,
  onInputChange,
  onArrayChange,
  onAddArrayItem,
  onRemoveArrayItem,
  onImageUpload,
  onRemoveImage,
  onSubmit,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50" data-testid="coach-form-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Add New Coach</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-6">
          {/* Language Tabs */}
          <div className="flex gap-2 border-b border-gray-200 pb-4">
            <button type="button" onClick={() => setActiveTab('en')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              EN English
            </button>
            <button type="button" onClick={() => setActiveTab('tr')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'tr' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              TR Turkish
            </button>
            <span className="ml-auto text-sm text-gray-500 self-center">
              {activeTab === 'en' ? 'Required fields' : 'Optional - falls back to English'}
            </span>
          </div>

          {/* Profile Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Photo</label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-dashed border-gray-300">
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Image className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div>
                <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={onImageUpload} />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} className="mb-2">
                  <Upload className="w-4 h-4 mr-2" /> Upload Photo
                </Button>
                {imagePreview && (
                  <Button type="button" variant="ghost" size="sm" onClick={onRemoveImage} className="text-red-500 hover:text-red-700 ml-2">
                    <X className="w-4 h-4 mr-1" /> Remove
                  </Button>
                )}
                <p className="text-xs text-gray-500 mt-1">JPG, PNG or GIF. Max 2MB.</p>
              </div>
            </div>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
              <Input name="name" value={formData.name} onChange={onInputChange} placeholder="e.g., Dr. Sarah Johnson" required data-testid="coach-name-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title {activeTab === 'en' && '*'}</label>
              <Input name={`title_${activeTab}`} value={formData[`title_${activeTab}`]} onChange={onInputChange}
                placeholder={activeTab === 'en' ? 'e.g., Executive Coach' : 'e.g., Yonetici Kocu'} required={activeTab === 'en'} data-testid="coach-title-input" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Bio {activeTab === 'en' && '*'}</label>
            <textarea name={`bio_${activeTab}`} value={formData[`bio_${activeTab}`]} onChange={onInputChange}
              placeholder={activeTab === 'en' ? 'Write a brief biography...' : 'Kisa bir biyografi yazin...'} rows={4}
              required={activeTab === 'en'} minLength={activeTab === 'en' ? 20 : 0}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="coach-bio-input" />
          </div>

          {/* Dynamic Array Fields */}
          <ArrayField label={`Specialties (${activeTab === 'en' ? 'English' : 'Turkish'})`} icon={<Briefcase className="w-4 h-4 inline mr-1" />}
            items={formData[`specialties_${activeTab}`]} field={`specialties_${activeTab}`}
            placeholder={activeTab === 'en' ? 'e.g., Executive Coaching' : 'e.g., Yonetici Koclugu'}
            addLabel={activeTab === 'en' ? 'Add Specialty' : 'Uzmanlik Ekle'}
            onArrayChange={onArrayChange} onAdd={onAddArrayItem} onRemove={onRemoveArrayItem} testIdPrefix="specialty" />

          <ArrayField label="Certifications" icon={<Award className="w-4 h-4 inline mr-1" />}
            items={formData.certifications} field="certifications" placeholder="e.g., ICF PCC"
            addLabel="Add Certification" onArrayChange={onArrayChange} onAdd={onAddArrayItem} onRemove={onRemoveArrayItem} testIdPrefix="certification" />

          <ArrayField label="Languages" icon={<Globe className="w-4 h-4 inline mr-1" />}
            items={formData.languages} field="languages" placeholder="e.g., English"
            addLabel="Add Language" onArrayChange={onArrayChange} onAdd={onAddArrayItem} onRemove={onRemoveArrayItem} testIdPrefix="language" />

          <div className="flex gap-4 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={isSubmitting} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="submit-coach-btn">
              {isSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding...</>) : 'Add Coach'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ArrayField = ({ label, icon, items, field, placeholder, addLabel, onArrayChange, onAdd, onRemove, testIdPrefix }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">{icon}{label}</label>
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={`${field}-${index}`} className="flex gap-2">
          <Input value={item} onChange={(e) => onArrayChange(field, index, e.target.value)}
            placeholder={placeholder} data-testid={`${testIdPrefix}-input-${index}`} />
          {items.length > 1 && (
            <Button type="button" variant="ghost" size="icon" onClick={() => onRemove(field, index)} className="text-red-500">
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onAdd(field)}>
        <Plus className="w-4 h-4 mr-1" /> {addLabel}
      </Button>
    </div>
  </div>
);

export default CoachForm;
